import React, { useState, useEffect } from 'react';
import { Button, Space, Modal, Form, Input, Select, Card } from 'antd';
import {
  PlusOutlined,
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Service } from '@/apis';
import type {
  UserDetailResponse,
  UserAddDto,
  UserUpdateDto,
  UserRoleBaseResData,
} from '@/apis';
import {
  DataStats,
  DataTable,
  type DataStat,
  type ActionButton,
} from '@/components/CommonTable';
import { useDataTableWithSearch } from '@/hooks/useDataTableWithSearch';
import { useFormModal } from '@/hooks/useFormModal';
import { SearchBar } from '@/components/SearchBar';
import {
  createTimeColumn,
  updateTimeColumn,
  createLongTextColumn,
} from '@/utils/commonUtils';
import styles from '@/components/CommonTable/index.module.less';

// 使用API返回的数据类型
type UserData = UserDetailResponse;

// 扩展UserAddDto类型以包含roleKeys字段
type UserAddDtoWithRoles = UserAddDto & {
  roleKeys?: string;
};

const CustomerUserManagement: React.FC = () => {
  // 角色列表状态
  const [roles, setRoles] = useState<UserRoleBaseResData[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // 辅助函数：从roleKeys字符串中提取有效的roleKey
  const getRoleKeysFromUser = (user: UserData): string[] => {
    if (user.roleKeys) {
      return user.roleKeys.split(',').filter(key => key.trim());
    }
    return [];
  };

  // 获取角色列表
  const fetchRoles = async () => {
    try {
      setRolesLoading(true);
      const response = await Service.userRoleControllerGet(1, 1000); // 获取所有角色
      if (response && response.data && response.data.rows) {
        setRoles(response.data.rows);
      }
    } catch (error) {
      console.error('获取角色列表失败:', error);
    } finally {
      setRolesLoading(false);
    }
  };

  // 组件挂载时获取角色列表
  useEffect(() => {
    fetchRoles();
  }, []);

  // 使用带搜索功能的表格管理Hook
  const {
    data: users,
    loading,
    pagination,
    fetchData: fetchUsers,
    handleDelete,
    handlePaginationChange,
    search,
  } = useDataTableWithSearch<UserData>({
    fetchData: (pageNum, pageSize, custom) =>
      Service.userControllerGet(pageNum, pageSize, custom),
    deleteData: async (id: number) => {
      await Service.userControllerDelete(id);
    },
    errorMessage: '获取用户列表失败',
    searchOptions: {
      fields: ['userName', 'realName', 'phone', 'uid', 'nickName'],
    },
  });

  // 使用Modal管理Hook
  const {
    visible,
    editingItem: editingUser,
    form,
    handleAdd,
    handleEdit: originalHandleEdit,
    handleOk,
    handleCancel,
  } = useFormModal<UserData>({
    createData: async (data: UserAddDtoWithRoles) => {
      // 处理多选角色数据
      if (data.roleKeys && Array.isArray(data.roleKeys)) {
        data.roleKeys = data.roleKeys.join(',');
      }
      await Service.userControllerAdd(data as UserAddDto);
    },
    updateData: async (data: UserUpdateDto) => {
      // 处理多选角色数据
      if (data.roleKeys && Array.isArray(data.roleKeys)) {
        data.roleKeys = data.roleKeys.join(',');
      }
      delete (data as Iobject).role;
      await Service.userControllerPut(data);
    },
  });

  // 自定义编辑处理函数，支持多角色
  const handleEdit = (record: UserData) => {
    // 处理角色字段：从roleKeys字符串中提取roleKey
    const roleKeys = getRoleKeysFromUser(record);

    const formData = {
      ...record,
      roleKeys: roleKeys.map(key => String(key.trim())), // 确保转换为字符串并去除空格
    };

    // 调试信息
    console.log('编辑用户数据:', record);
    console.log('roleKeys字符串:', record.roleKeys);
    console.log('解析后的角色Key数组:', roleKeys);
    console.log('表单数据:', formData);
    console.log('可用角色列表:', roles);

    // 先调用原始的编辑处理函数设置基础状态
    originalHandleEdit(record);
    // 然后设置处理过的表单数据
    setTimeout(() => {
      form.setFieldsValue(formData);
    }, 0);
  };

  // 处理Modal确认后的数据刷新
  const handleModalOkWithRefresh = async () => {
    const success = await handleOk();
    if (success) {
      fetchUsers();
    }
  };

  const columns: ColumnsType<UserData> = [
    createLongTextColumn('用户名', 'userName', 8),
    createLongTextColumn('真实姓名', 'realName', 8),
    createLongTextColumn('昵称', 'nickName', 8),
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
    },
    createLongTextColumn('邮箱', 'email', 12),
    createLongTextColumn('UID', 'uid', 12),
    {
      title: '角色',
      dataIndex: 'roleKeys',
      key: 'roleKeys',
      render: (_, record: UserData) => {
        const keys = getRoleKeysFromUser(record);
        if (keys.length === 0) return '-';

        if (keys.length === 1) {
          // 单个角色，使用roleKey查找角色
          const role = roles.find(r => r.roleKey === keys[0]);
          const roleName = role ? role.roleName : keys[0];

          const style = {
            color: roleName === '超级管理员' ? '#cf1322' : '#1890ff',
            backgroundColor: roleName === '超级管理员' ? '#fff2f0' : '#f0f5ff',
            border: `1px solid ${roleName === '超级管理员' ? '#ffccc7' : '#d6e4ff'}`,
            borderRadius: '4px',
            padding: '2px 8px',
            fontSize: '12px',
            display: 'inline-block',
          };

          return <span style={style}>{roleName}</span>;
        } else {
          // 多个角色，显示为标签组
          const roleNames = keys.map(roleKey => {
            const role = roles.find(r => r.roleKey === roleKey);
            return role ? role.roleName : roleKey;
          });

          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {roleNames.map((roleName, index) => (
                <span
                  key={index}
                  style={{
                    color: roleName === '超级管理员' ? '#cf1322' : '#1890ff',
                    backgroundColor:
                      roleName === '超级管理员' ? '#fff2f0' : '#f0f5ff',
                    border: `1px solid ${roleName === '超级管理员' ? '#ffccc7' : '#d6e4ff'}`,
                    borderRadius: '4px',
                    padding: '2px 8px',
                    fontSize: '12px',
                    display: 'inline-block',
                  }}
                >
                  {roleName}
                </span>
              ))}
            </div>
          );
        }
      },
    },
    createTimeColumn('full'),
    updateTimeColumn('full'),
  ];

  // 统计卡片数据
  const stats: DataStat[] = [
    {
      title: '总用户数',
      value: pagination.total,
      prefix: <UserOutlined />,
      valueStyle: { color: '#3f8600' },
    },
    {
      title: '当前页用户',
      value: users.length,
      valueStyle: { color: '#1890ff' },
    },
    {
      title: '管理员',
      value: users.filter(u => {
        const keys = getRoleKeysFromUser(u);
        return keys.some(k => {
          const role = roles.find(r => r.roleKey === k);
          return role && role.roleName === '超级管理员';
        });
      }).length,
      valueStyle: { color: '#cf1322' },
    },
    {
      title: '普通用户',
      value: users.filter(u => {
        const keys = getRoleKeysFromUser(u);
        return keys.some(k => {
          const role = roles.find(r => r.roleKey === k);
          return role && role.roleName === '普通用户';
        });
      }).length,
      valueStyle: { color: '#722ed1' },
    },
  ];

  // 操作按钮配置
  const actionButtons: ActionButton<UserData>[] = [
    {
      key: 'edit',
      label: '编辑',
      icon: <EditOutlined />,
      onClick: handleEdit,
    },
    {
      key: 'delete',
      label: '删除',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: record => handleDelete(record.id),
      confirm: {
        title: '确定要删除这个用户吗？',
        okText: '确定',
        cancelText: '取消',
      },
    },
  ];

  return (
    <div className={styles.dataTablePage}>
      <Card className={styles.card}>
        {/* 统计卡片 */}
        <DataStats stats={stats} className={styles.statsRow} />

        {/* 操作栏 */}
        <div className={styles.toolbar}>
          <Space wrap>
            <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
              新增用户
            </Button>

            {/* 搜索区域 */}
            <SearchBar
              search={search}
              placeholder='搜索用户名、真实姓名、昵称、手机号、邮箱或UID'
              width={300}
              loading={loading}
            />
          </Space>
        </div>

        {/* 用户表格 */}
        <DataTable
          columns={columns}
          dataSource={users}
          loading={loading}
          rowKey='id'
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          actionButtons={actionButtons}
        />
      </Card>

      {/* 新增/编辑用户弹窗 */}
      <Modal
        title={editingUser ? '编辑用户' : '新增用户'}
        open={visible}
        onOk={handleModalOkWithRefresh}
        onCancel={handleCancel}
        width={600}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='userName'
            label='用户名'
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input placeholder='请输入用户名' />
          </Form.Item>
          <Form.Item
            name='realName'
            label='真实姓名'
            rules={[{ required: true, message: '请输入真实姓名' }]}
          >
            <Input placeholder='请输入真实姓名' />
          </Form.Item>
          <Form.Item
            name='nickName'
            label='昵称'
            rules={[{ required: true, message: '请输入昵称' }]}
          >
            <Input placeholder='请输入昵称' />
          </Form.Item>
          <Form.Item
            name='phone'
            label='手机号'
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
            ]}
          >
            <Input placeholder='请输入手机号' />
          </Form.Item>
          <Form.Item
            name='email'
            label='邮箱'
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入正确的邮箱格式' },
            ]}
          >
            <Input placeholder='请输入邮箱' />
          </Form.Item>
          <Form.Item
            name='password'
            label='密码'
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password placeholder='请输入密码' />
          </Form.Item>
          <Form.Item
            name='roleKeys'
            label='角色'
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select
              mode='multiple'
              placeholder='请选择角色'
              loading={rolesLoading}
              showSearch
              optionFilterProp='children'
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
              maxTagCount='responsive'
            >
              {roles.map(role => (
                <Select.Option key={role.roleKey} value={role.roleKey}>
                  {role.roleName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomerUserManagement;
